import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { formatPatientAge, normalizePatientAge } from '../../../utils/patient-age.util';

@Component({
  selector: 'app-patient-age-fields',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './patient-age-fields.component.html',
  styleUrl: './patient-age-fields.component.scss',
})
export class PatientAgeFieldsComponent {
  @Input() years: number | string | null = '';
  @Output() yearsChange = new EventEmitter<number | string | null>();

  @Input() months: number | string | null = '';
  @Output() monthsChange = new EventEmitter<number | string | null>();

  @Input() days: number | string | null = '';
  @Output() daysChange = new EventEmitter<number | string | null>();

  @Input() compact = false;
  @Input() showPreview = true;
  @Input() maxYears = 130;
  @Input() disabled = false;

  onYearsInput(event: Event): void {
    this.years = this.sanitizeUnit((event.target as HTMLInputElement).value, this.maxYears);
    this.yearsChange.emit(this.years);
    this.emitNormalized();
  }

  onMonthsInput(event: Event): void {
    this.months = this.sanitizeUnit((event.target as HTMLInputElement).value, 11);
    this.monthsChange.emit(this.months);
    this.emitNormalized();
  }

  onDaysInput(event: Event): void {
    // Allow typing 30+ so normalization can roll days into months.
    this.days = this.sanitizeUnit((event.target as HTMLInputElement).value, 364);
    this.daysChange.emit(this.days);
    this.emitNormalized();
  }

  get previewLabel(): string {
    return formatPatientAge(this.years, this.months, this.days);
  }

  private emitNormalized(): void {
    const normalized = normalizePatientAge({
      years: this.years,
      months: this.months,
      days: this.days,
    });

    if (String(this.years ?? '') !== String(normalized.years || '')) {
      this.years = normalized.years || '';
      this.yearsChange.emit(this.years);
    }
    if (String(this.months ?? '') !== String(normalized.months || '')) {
      this.months = normalized.months || '';
      this.monthsChange.emit(this.months);
    }
    if (String(this.days ?? '') !== String(normalized.days || '')) {
      this.days = normalized.days || '';
      this.daysChange.emit(this.days);
    }
  }

  private sanitizeUnit(value: string, max: number): number | '' {
    const digits = String(value ?? '').replace(/\D/g, '');
    if (!digits) {
      return '';
    }
    const parsed = parseInt(digits, 10);
    if (!Number.isFinite(parsed)) {
      return '';
    }
    return Math.min(max, Math.max(0, parsed));
  }
}
